import React, { FC, useEffect } from 'react';
import View from './view';
import Summary from './summary';
import Rewards from './rewards';
import {
  generatePath,
  useHistory,
  useLocation,
  useRouteMatch
} from 'react-router';
import CustomTab from 'components/shared/CustomTab';
import AssetManagement from 'components/crucible/detail/assetManagement';
import ReadonlySummary from 'components/explore/Summary';
import { TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/tabs';
import { useBreakpointValue } from '@chakra-ui/react';
import { Crucible } from 'types';
import { Box } from '@chakra-ui/layout';
import { FiEye, FiGrid } from 'react-icons/fi';
import { BiTransfer } from 'react-icons/bi';
import { GiReceiveMoney } from 'react-icons/gi';
import { useTokenBlacklist } from 'hooks/query/useTokenBlacklist';

type Props = {
  crucible: Crucible;
};

const CrucibleTabs: FC<Props> = ({ crucible }) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const [tabIndex, setTabIndex] = React.useState(0);
  const { data: tokenBlacklist = [] } = useTokenBlacklist();
  const [transferTabIndex, setTransferTabIndex] = React.useState(
    //@ts-ignore
    (history.location.state?.tab as number) || 0
  );
  const match = useRouteMatch([
    '/explore/:crucibleId/:tab?',
    '/crucible/:crucibleId/:tab?'
  ]) as any;

  const isExploreMatch = history.location.pathname.includes('/explore');

  const iconProps = { fontSize: '24px' };
  const summaryDescriptor = useBreakpointValue({
    base: <FiGrid {...iconProps} />,
    md: 'Summary'
  });
  const assetsDescriptor = useBreakpointValue({
    base: <BiTransfer {...iconProps} />,
    md: 'Asset Management'
  });
  const rewardsDescriptor = useBreakpointValue({
    base: <GiReceiveMoney {...iconProps} />,
    md: 'Rewards'
  });
  const viewDescriptor = useBreakpointValue({
    base: <FiEye {...iconProps} />,
    md: 'View'
  });

  const tabs = [
    'summary',
    !isExploreMatch && 'asset-management',
    'rewards',
    'view'
  ].filter(Boolean);

  const handleTabChange = (index: number) => {
    setTabIndex(index);
    history.replace(
      generatePath(match.path, { ...match.params, tab: tabs[index] }) +
        window.location.search
    );
  };

  useEffect(() => {
    const tabIndex = tabs.indexOf(String(match?.params?.tab));
    if (tabIndex > -1) {
      setTabIndex(tabIndex);
    } else {
      setTabIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.params?.tab, setTabIndex, pathname]);

  return (
    <Box>
      <Tabs
        noOfLines={1}
        align='center'
        index={tabIndex}
        isLazy
        onChange={handleTabChange}
      >
        <TabList borderRadius='xl' border='none' pt={2} pb={4}>
          <CustomTab>{summaryDescriptor}</CustomTab>
          {!isExploreMatch && <CustomTab>{assetsDescriptor}</CustomTab>}
          <CustomTab>{rewardsDescriptor}</CustomTab>
          <CustomTab>{viewDescriptor}</CustomTab>
        </TabList>
        <TabPanels>
          <TabPanel px={0} pb={0}>
            {/* TODO: Use single dynamic component */}
            {!isExploreMatch ? (
              <Summary
                crucible={crucible}
                setTabIndex={setTabIndex}
                setTransferTabIndex={setTransferTabIndex}
                tokenBlacklist={tokenBlacklist}
              />
            ) : (
              <ReadonlySummary
                crucible={crucible}
                setTabIndex={setTabIndex}
                setTransferTabIndex={setTransferTabIndex}
              />
            )}
          </TabPanel>
          {!isExploreMatch && (
            <TabPanel px={0} pb={0}>
              <AssetManagement
                crucible={crucible}
                transferTabIndex={transferTabIndex}
                setTransferTabIndex={setTransferTabIndex}
              />
            </TabPanel>
          )}
          <TabPanel px={0} pb={0}>
            <Rewards
              crucible={crucible}
              setTabIndex={setTabIndex}
              setTransferTabIndex={setTransferTabIndex}
            />
          </TabPanel>
          <TabPanel px={0} pb={0}>
            <View crucible={crucible} readOnly={isExploreMatch} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default CrucibleTabs;
